import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/globals.css";

import { AppShell } from "@/components/AppShell";
import { AuthGate } from "@/components/AuthGate";
import { ToastProvider } from "@/components/system";
import { Today } from "@/routes/Today";
import { Placeholder } from "@/routes/Placeholder";
import { SignIn } from "@/routes/SignIn";
import { AuthCallback } from "@/routes/AuthCallback";
import { Onboarding } from "@/routes/Onboarding";
import { Pantry } from "@/routes/Pantry";
import { PantryDetail } from "@/routes/PantryDetail";
import { Kitchen } from "@/routes/Kitchen";
import { ImportCronometer } from "@/routes/ImportCronometer";
import { Health } from "@/routes/Health";
import { HealthBody } from "@/routes/HealthBody";
import { HealthRecovery } from "@/routes/HealthRecovery";
import { HealthRedS } from "@/routes/HealthRedS";
import { HealthGlp1 } from "@/routes/HealthGlp1";
import { HealthTraining } from "@/routes/HealthTraining";
import { HealthNutrition } from "@/routes/HealthNutrition";
import { HealthPrograms } from "@/routes/HealthPrograms";
import { Integrations } from "@/routes/Integrations";
import { Settings } from "@/routes/Settings";
import { Calendar } from "@/routes/Calendar";
import { CalendarDay } from "@/routes/CalendarDay";
import { CalendarWeek } from "@/routes/CalendarWeek";
import { CalendarYear } from "@/routes/CalendarYear";
import { Documents } from "@/routes/Documents";
import { DocumentDetail } from "@/routes/DocumentDetail";
import { DocumentNew } from "@/routes/DocumentNew";
import { Projects } from "@/routes/Projects";
import { ProjectDetail } from "@/routes/ProjectDetail";
import { Evidence } from "@/routes/Evidence";
import { People } from "@/routes/People";
import { PersonDetail } from "@/routes/PersonDetail";
import { Threads } from "@/routes/Threads";
import { ThreadDetail } from "@/routes/ThreadDetail";
import { Finance } from "@/routes/Finance";
import { FinanceAccounts } from "@/routes/FinanceAccounts";
import { FinanceTransactions, FinanceTransactionNew } from "@/routes/FinanceTransactions";
import { FinanceBudget } from "@/routes/FinanceBudget";
import { FinanceReceipts } from "@/routes/FinanceReceipts";
import { FinanceSavings } from "@/routes/FinanceSavings";
import { FinanceInvestments } from "@/routes/FinanceInvestments";
import { Recipes } from "@/routes/Recipes";
import { RecipeDetail } from "@/routes/RecipeDetail";
import { RecipeNew } from "@/routes/RecipeNew";
import { MealPlan } from "@/routes/MealPlan";
import { ShoppingList } from "@/routes/ShoppingList";
import { Learning } from "@/routes/Learning";
import { LearningVocabulary } from "@/routes/LearningVocabulary";
import { LearningVocabularyImport } from "@/routes/LearningVocabularyImport";
import { LearningVocabularyStats } from "@/routes/LearningVocabularyStats";
import { DocsGarminSetup } from "@/routes/DocsGarminSetup";
import { Inbox } from "@/routes/Inbox";
import { Tasks } from "@/routes/Tasks";
import { LogMealsDay } from "@/routes/LogMealsDay";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createBrowserRouter([
  {
    path: "/sign-in",
    element: <SignIn />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    // Onboarding requires a session but NOT yet an AthleteProfile.
    path: "/onboarding",
    element: (
      <AuthGate requireProfile={false}>
        <Onboarding />
      </AuthGate>
    ),
  },
  {
    path: "/",
    element: (
      <AuthGate>
        <AppShell />
      </AuthGate>
    ),
    children: [
      { index: true, element: <Today /> },
      { path: "inbox", element: <Inbox /> },
      { path: "tasks", element: <Tasks /> },
      { path: "calendar", element: <Calendar /> },
      { path: "calendar/day/:date", element: <CalendarDay /> },
      { path: "calendar/week/:week", element: <CalendarWeek /> },
      { path: "calendar/month/:month", element: <Calendar /> },
      { path: "calendar/year/:year", element: <CalendarYear /> },
      // Legacy: /calendar/:date — keep working for old links + iCal redirects.
      { path: "calendar/:date", element: <CalendarDay /> },
      { path: "documents", element: <Documents /> },
      { path: "documents/new", element: <DocumentNew /> },
      { path: "documents/:id", element: <DocumentDetail /> },
      { path: "health", element: <Health /> },
      { path: "health/body", element: <HealthBody /> },
      { path: "health/recovery", element: <HealthRecovery /> },
      { path: "health/red-s", element: <HealthRedS /> },
      { path: "health/glp1", element: <HealthGlp1 /> },
      { path: "health/training", element: <HealthTraining /> },
      { path: "health/nutrition", element: <HealthNutrition /> },
      { path: "health/programs", element: <HealthPrograms /> },
      { path: "kitchen", element: <Kitchen /> },
      { path: "pantry", element: <Pantry /> },
      { path: "pantry/:id", element: <PantryDetail /> },
      { path: "evidence", element: <Evidence /> },
      { path: "projects", element: <Projects /> },
      { path: "projects/:id", element: <ProjectDetail /> },
      { path: "learning", element: <Learning /> },
      { path: "learning/vocabulary", element: <LearningVocabulary /> },
      { path: "learning/vocabulary/import", element: <LearningVocabularyImport /> },
      { path: "learning/vocabulary/stats", element: <LearningVocabularyStats /> },
      { path: "people", element: <People /> },
      { path: "people/new", element: <PersonDetail /> },
      { path: "people/:id", element: <PersonDetail /> },
      { path: "threads", element: <Threads /> },
      { path: "threads/new", element: <ThreadDetail /> },
      { path: "threads/:id", element: <ThreadDetail /> },
      { path: "finance", element: <Finance /> },
      { path: "finance/accounts", element: <FinanceAccounts /> },
      { path: "finance/transactions", element: <FinanceTransactions /> },
      { path: "finance/transactions/new", element: <FinanceTransactionNew /> },
      { path: "finance/budget", element: <FinanceBudget /> },
      { path: "finance/receipts", element: <FinanceReceipts /> },
      { path: "finance/savings", element: <FinanceSavings /> },
      { path: "finance/investments", element: <FinanceInvestments /> },
      { path: "recipes", element: <Recipes /> },
      { path: "recipes/new", element: <RecipeNew /> },
      { path: "recipes/:id", element: <RecipeDetail /> },
      { path: "meal-plan", element: <MealPlan /> },
      { path: "shopping", element: <ShoppingList /> },
      { path: "integrations", element: <Integrations /> },
      { path: "docs/integrations/garmin", element: <DocsGarminSetup /> },
      { path: "settings", element: <Settings /> },
      { path: "settings/integrations", element: <Integrations /> },
      { path: "import/cronometer", element: <ImportCronometer /> },
      { path: "log/meals", element: <LogMealsDay /> },
      { path: "log/meals/:date", element: <LogMealsDay /> },
      { path: "more", element: <Placeholder title="More" /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
